/**
 * One occurrence's role-filtered projection (issue #321 Milestone 1).
 *
 * QUERY KEY carries tenant slug AND user id for the same reason the dashboard
 * hooks do: React Query's cache outlives the auth lifecycle, so a same-tenant
 * sign-out-and-different-user-sign-in in one tab would otherwise serve the first
 * person's session workspace — including a teaching payload with course
 * defaults and provenance — to the second.
 *
 * A CACHED JOIN SURVIVES ONLY WHILE THE KEY DOES. The design permits keeping a
 * rendered workspace through a failed background refresh, with `Could not
 * refresh` and its last-updated time, but an authorization failure must clear
 * it. `retry: false` on a 4xx plus the identity-bearing key is what makes both
 * true: a 401/403/404 is not retried into a spinner, and it cannot be answered
 * from another identity's cache entry.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import { currentOnConflict } from "@/lib/api/conflict";
import { attendanceApi, type MeetingConfigPatch, type Session } from "@/lib/api/attendance";
import { sessionPortalApi, type SessionWorkspace } from "@/lib/api/sessionPortal";
import { tenantSlugFromHostname } from "@/lib/tenant";

export function sessionWorkspaceKey(sessionId: string, userId: string | undefined) {
  return ["session-workspace", tenantSlugFromHostname(), userId ?? "anon", sessionId];
}

export function useSessionWorkspace(sessionId: string | undefined) {
  const { me } = useAuth();
  return useQuery({
    queryKey: sessionWorkspaceKey(sessionId ?? "", me?.user_id),
    queryFn: () => sessionPortalApi.workspace(sessionId as string),
    enabled: Boolean(sessionId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // A refusal is an answer, not a transient failure. Retrying a 404 would
    // spin on the one response the portal deliberately gives for a missing
    // session, a wrong tenant, a missing relationship, and a disabled feature.
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
  });
}

/**
 * The destination write. It is the EXISTING session override endpoint, not a
 * portal one: ADR 0016 keeps `attendance.py` the write owner and this milestone
 * adds only provenance and the concurrency precondition to it.
 *
 * `expected_updated_at` is always sent. The server applies its own read-then-
 * write precondition regardless, so the transaction is safe without it; sending
 * it is what turns "your edit silently replaced a colleague's correction" into
 * a 409 the instructor can act on.
 */
export interface DestinationSave extends MeetingConfigPatch {
  organizer_ownership?: "tenant_workspace" | "external_or_personal" | "unknown";
  note?: string | null;
  expected_updated_at: string;
}

export function useSaveSessionDestination(sessionId: string) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<Session, ApiError, DestinationSave>({
    mutationFn: (body) => attendanceApi.updateSessionMeeting(sessionId, body),
    onSuccess: () => {
      // The workspace projection, and every dashboard summary derived from the
      // same resolved destination, are now stale. Invalidate rather than patch
      // the cache: `destination_state` and `allowed_actions` are server
      // derivations, and reconstructing them here would be the sixth readiness
      // implementation this milestone exists to remove.
      void queryClient.invalidateQueries({
        queryKey: sessionWorkspaceKey(sessionId, me?.user_id),
      });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** The newest effective destination a 409 carries beside its message. */
export function conflictCurrent(error: unknown): Session | null {
  return currentOnConflict<Session>(error);
}

export type { SessionWorkspace };
